import React, { useState, useRef, useEffect } from "react";
import {
  Download,
  Type,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  Lock,
  Clock,
} from "lucide-react";
import templatesData from "../templates.json";
import "../NameOverlayApp.css";

const NameOverlayApp = ({
  templates: propTemplates,
  onDownload,
  onDataSubmit,
  className = "",
  showInstructions = true,
}) => {
  const [userName, setUserName] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [userPlace, setUserPlace] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const canvasRef = useRef(null);

  // Update current time every minute to check for newly available templates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Filter templates based on availability datetime
  const getAvailableTemplates = () => {
    const allTemplates = propTemplates || templatesData.templates;
    const now = currentTime;

    // Separate available and locked templates
    const availableTemplates = allTemplates.filter((template) => {
      if (!template.availableFrom) return true; // If no availableFrom, assume always available
      const availableDate = new Date(template.availableFrom);
      return now >= availableDate;
    });

    const lockedTemplates = allTemplates
      .filter((template) => {
        if (!template.availableFrom) return false;
        const availableDate = new Date(template.availableFrom);
        return now < availableDate;
      })
      .map((template) => ({
        ...template,
        locked: true,
        availableDate: new Date(template.availableFrom),
      }));

    // Sort available templates by ID (ascending)
    availableTemplates.sort((a, b) => b.id - a.id);

    // Sort locked templates by availability date (earliest first)
    lockedTemplates.sort((a, b) => a.availableDate - b.availableDate);

    return {
      available: availableTemplates,
      locked: lockedTemplates,
      all: [...availableTemplates, ...lockedTemplates],
      totalTemplates: allTemplates.length,
    };
  };

  const {
    available: availableTemplates,
    locked: lockedTemplates,
    all: allTemplates,
    totalTemplates,
  } = getAvailableTemplates();

  // Reset selected template if current selection is locked
  useEffect(() => {
    if (allTemplates[selectedTemplate]?.locked) {
      const firstAvailableIndex = allTemplates.findIndex(
        (template) => !template.locked
      );
      if (firstAvailableIndex !== -1) {
        setSelectedTemplate(firstAvailableIndex);
      }
    }
  }, [allTemplates, selectedTemplate]);

  const currentTemplate = allTemplates[selectedTemplate];
  const isCurrentTemplateLocked = currentTemplate?.locked;

  const validateTemplate = (template) => {
    const requiredFields = [
      "id",
      "name",
      "imageUrl",
      "textCoordinates",
      "fontSize",
      "fontColor",
      "fontFamily",
    ];

    for (const field of requiredFields) {
      if (!template[field]) {
        throw new Error(`Template missing required field: ${field}`);
      }
    }

    if (
      typeof template.textCoordinates.x !== "number" ||
      typeof template.textCoordinates.y !== "number"
    ) {
      throw new Error(
        "Template textCoordinates must have numeric x and y values"
      );
    }

    return true;
  };

  useEffect(() => {
    availableTemplates.forEach((template) => {
      try {
        validateTemplate(template);
      } catch (error) {
        console.error(`Invalid template: ${template.name}`, error.message);
      }
    });
  }, [availableTemplates]);

  const drawImageWithText = async () => {
    if (isCurrentTemplateLocked) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (currentTemplate.fontFamily === "Poppins") {
      await document.fonts.load(`${currentTemplate.fontSize}px Poppins`);
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = 1080;
        canvas.height = 1350;
        ctx.drawImage(img, 0, 0, 1080, 1350);

        if (userName.trim()) {
          const fontFamily =
            currentTemplate.fontFamily === "Poppins"
              ? `"Poppins", Arial, sans-serif`
              : currentTemplate.fontFamily;

          ctx.font = `${currentTemplate.fontSize}px ${fontFamily}`;
          ctx.fillStyle = currentTemplate.fontColor;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;

          ctx.fillText(
            userName,
            currentTemplate.textCoordinates.x,
            currentTemplate.textCoordinates.y
          );
        }
        resolve();
      };
      img.src = currentTemplate.imageUrl;
    });
  };

  const handleDownload = async () => {
    if (isCurrentTemplateLocked) {
      const availableDate = currentTemplate.availableDate;
      const timeLeft = availableDate - currentTime;
      const hoursLeft = Math.ceil(timeLeft / (1000 * 60 * 60));
      alert(`This template will be available in ${hoursLeft} hour(s)!`);
      return;
    }

    if (!userName.trim()) {
      alert("Please enter your name first!");
      return;
    }

    if (!whatsappNumber.trim()) {
      alert("Please enter your WhatsApp number!");
      return;
    }

    if (!userPlace.trim()) {
      alert("Please enter your place!");
      return;
    }

    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(whatsappNumber.replace(/[\s\-\(\)]/g, ""))) {
      alert("Please enter a valid WhatsApp number!");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("Saving your information...");

    const userData = {
      name: userName.trim(),
      whatsappNumber: whatsappNumber.trim(),
      place: userPlace.trim(),
      templateId: currentTemplate.id,
      templateName: currentTemplate.name,
      timestamp: new Date().toISOString(),
      availableTemplatesCount: availableTemplates.length,
    };

    try {
      if (onDataSubmit) {
        await onDataSubmit(userData);
        setSubmitStatus("Information saved successfully!");
      } else {
        setSubmitStatus("Preparing your certificate...");
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await drawImageWithText();

      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL();
      const fileName = `${userName.replace(
        /\s+/g,
        "_"
      )}_${currentTemplate.name.replace(/\s+/g, "_")}.png`;

      if (onDownload) {
        onDownload({ dataUrl, fileName, userData, template: currentTemplate });
      } else {
        const link = document.createElement("a");
        link.download = fileName;
        link.href = dataUrl;
        link.click();
      }

      setSubmitStatus("Download started!");
    } catch (error) {
      console.error("Failed to submit user data:", error);
      setSubmitStatus(
        "Failed to save information. Proceeding with download..."
      );

      await new Promise((resolve) => setTimeout(resolve, 1000));
      await drawImageWithText();

      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL();
      const fileName = `${userName.replace(
        /\s+/g,
        "_"
      )}_${currentTemplate.name.replace(/\s+/g, "_")}.png`;

      const link = document.createElement("a");
      link.download = fileName;
      link.href = dataUrl;
      link.click();
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(""), 3000);
    }
  };

  const handlePreview = async () => {
    if (!isCurrentTemplateLocked) {
      await drawImageWithText();
    }
  };

  const navigateTemplate = (direction) => {
    let newIndex;
    if (direction === "prev") {
      newIndex =
        selectedTemplate === 0 ? allTemplates.length - 1 : selectedTemplate - 1;
    } else {
      newIndex =
        selectedTemplate === allTemplates.length - 1 ? 0 : selectedTemplate + 1;
    }
    setSelectedTemplate(newIndex);
  };

  useEffect(() => {
    if (userName && !isCurrentTemplateLocked) {
      handlePreview();
    }
  }, [userName, selectedTemplate, isCurrentTemplateLocked]);

  if (!allTemplates || allTemplates.length === 0) {
    return (
      <div className={`name-overlay-app ${className}`}>
        <div className="container">
          <div className="header">
            <h1 className="title">Get your personalized certificate</h1>
            <p className="subtitle">No templates available</p>
          </div>
        </div>
      </div>
    );
  }

  const formatDateTime = (date) => {
    return date.toLocaleString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeRemaining = (targetDate) => {
    const now = currentTime;
    const diff = targetDate - now;

    if (diff <= 0) return "Available now";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day(s) remaining`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  return (
    <div className={`name-overlay-app ${className}`}>
      <div className="container">
        <div className="header">
          <h1 className="title">Get your personalized certificate</h1>
          <p className="subtitle">
            Enter your name, and download your personalized certificate
          </p>
        </div>

        <div className="main-content">
          <div className="controls-panel">
            <h2 className="panel-title">
              <Type className="icon" />
              Customize Your Certificate
            </h2>

            <div className="section">
              <label className="label">
                Choose Template ({selectedTemplate + 1} of {allTemplates.length}
                )
                {isCurrentTemplateLocked && (
                  <span className="unlock-info">
                    <Lock className="lock-icon" />
                    Available {formatDateTime(currentTemplate.availableDate)}
                  </span>
                )}
              </label>
              <div className="carousel-container">
                <button
                  className="carousel-nav prev"
                  onClick={() => navigateTemplate("prev")}
                  aria-label="Previous template"
                >
                  <ChevronLeft />
                </button>

                <div className="carousel">
                  <div
                    className="carousel-track"
                    style={{
                      transform: `translateX(-${selectedTemplate * 100}%)`,
                    }}
                  >
                    {allTemplates.map((template, index) => (
                      <div key={template.id} className="carousel-slide">
                        <div
                          className={`template-preview ${
                            template.locked ? "locked" : ""
                          }`}
                        >
                          <img
                            src={template.imageUrl}
                            alt={template.name}
                            className="template-image"
                            loading="lazy"
                            style={{ opacity: template.locked ? 0.5 : 1 }}
                          />
                          {template.locked && (
                            <div className="lock-overlay">
                              <Lock className="lock-icon-large" />
                              <p className="unlock-text">
                                {formatDateTime(template.availableDate)}
                              </p>
                              <p className="time-remaining">
                                {getTimeRemaining(template.availableDate)}
                              </p>
                            </div>
                          )}
                          <div className="template-overlay">
                            <h4 className="template-title">{template.name}</h4>
                            <p className="template-specs">
                              {template.fontFamily} • {template.fontSize}px
                              {template.locked && (
                                <span className="availability-badge">
                                  {getTimeRemaining(template.availableDate)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  className="carousel-nav next"
                  onClick={() => navigateTemplate("next")}
                  aria-label="Next template"
                >
                  <ChevronRight />
                </button>
              </div>

              {/* <div className="carousel-indicators">
                {allTemplates.map((template, index) => (
                  <button
                    key={index}
                    className={`indicator ${
                      selectedTemplate === index ? "active" : ""
                    } ${template.locked ? "locked" : ""}`}
                    onClick={() => setSelectedTemplate(index)}
                    aria-label={`Go to template ${index + 1}`}
                  />
                ))}
              </div> */}
            </div>

            <div className="section">
              <label className="label" htmlFor="nameInput">
                Enter Your Name *
              </label>
              <input
                id="nameInput"
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Type your name here..."
                className="name-input"
                maxLength={50}
                required
                disabled={isCurrentTemplateLocked}
              />
            </div>

            <div className="section">
              <label className="label" htmlFor="whatsappInput">
                WhatsApp Number *
              </label>
              <input
                id="whatsappInput"
                type="tel"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="+91 9876543210"
                className="name-input"
                maxLength={20}
                required
                disabled={isCurrentTemplateLocked}
              />
              <p className="input-hint">
                Include country code (e.g., +91 for India)
              </p>
            </div>

            <div className="section">
              <label className="label" htmlFor="placeInput">
                Your Place *
              </label>
              <input
                id="placeInput"
                type="text"
                value={userPlace}
                onChange={(e) => setUserPlace(e.target.value)}
                placeholder="City, State"
                className="name-input"
                maxLength={100}
                required
                disabled={isCurrentTemplateLocked}
              />
            </div>

            <button
              onClick={handleDownload}
              disabled={
                !userName.trim() ||
                !whatsappNumber.trim() ||
                !userPlace.trim() ||
                isSubmitting ||
                isCurrentTemplateLocked
              }
              className={`download-button ${
                isCurrentTemplateLocked ? "locked" : ""
              }`}
              aria-label="Download personalized certificate"
            >
              {isCurrentTemplateLocked ? (
                <>
                  <Lock className="icon" />
                  Template Locked
                </>
              ) : (
                <>
                  <Download className="icon" />
                  {isSubmitting ? "Processing..." : "Download Certificate"}
                </>
              )}
            </button>

            {isCurrentTemplateLocked && (
              <div className="lock-message">
                This template will be available on{" "}
                {formatDateTime(currentTemplate.availableDate)}
                <br />
                <strong>
                  {getTimeRemaining(currentTemplate.availableDate)}
                </strong>
              </div>
            )}

            {submitStatus && (
              <div
                className={`status-message ${
                  submitStatus.includes("Failed") ? "error" : "success"
                }`}
              >
                {submitStatus}
              </div>
            )}

            <p className="form-note">
              * All fields are required. New templates unlock automatically!
            </p>
          </div>

          <div className="preview-panel">
            <h2 className="panel-title">Preview</h2>

            <div className="preview-area">
              {!isCurrentTemplateLocked ? (
                <canvas
                  ref={canvasRef}
                  className="preview-canvas"
                  style={{ display: userName ? "block" : "none" }}
                  aria-label="Image preview with your name"
                />
              ) : (
                <div className="locked-preview">
                  <Lock className="locked-preview-icon" />
                  <p>Template preview locked</p>
                  <p>{formatDateTime(currentTemplate.availableDate)}</p>
                  <p className="countdown">
                    {getTimeRemaining(currentTemplate.availableDate)}
                  </p>
                </div>
              )}

              {!userName && !isCurrentTemplateLocked && (
                <div className="empty-state">
                  <ImageIcon className="empty-icon" />
                  <p className="empty-title">Enter your name to see preview</p>
                  <p className="empty-subtitle">
                    Your personalized certificate will appear here
                  </p>
                </div>
              )}
            </div>

            {userName && !isCurrentTemplateLocked && (
              <div className="preview-info">
                <p>
                  Preview of "{userName}" on {currentTemplate.name}
                </p>
                {whatsappNumber && userPlace && (
                  <p className="user-details">
                    WhatsApp: {whatsappNumber} • Place: {userPlace}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {showInstructions && (
          <div className="instructions">
            <h3>How to Use</h3>
            <div className="steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <div className="step-title">Enter Your Details</div>
                  <div className="step-description">
                    Fill in your name, WhatsApp number, and location for your
                    certificate
                  </div>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <div className="step-title">Download</div>
                  <div className="step-description">
                    Download your personalized 1350x1080 certificate as PNG
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NameOverlayApp;
